/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, Reducer, useCallback, useContext, useReducer as useReactReducer } from 'react'

import { InspectorContext } from '../InspectorContext'
import { getDevTools } from '../utils'
import { Listener, type Subscribe, useQueuedDispatch } from './useQueuedDispatch'

//this is a copy of the react useReducer hook typing with the addition of the name parameter
type EnhancedReducerOptions<S> = {
  /**
   * The name for the reducer instance in Redux DevTools.
   * When passed as a function, it must be a stable function to maintain a stable `dispatch` reference.
   */
  name?: string | ((state: S) => string)
}

export function useReducer<S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
  options?: EnhancedReducerOptions<S>
): [S, Dispatch<A>, Subscribe<S, A>]
export function useReducer<S, A, I>(
  reducer: Reducer<S, A>,
  initialState: I,
  initializer: (arg: I) => S,
  options?: EnhancedReducerOptions<S>
): [S, Dispatch<A>, Subscribe<S, A>]
export function useReducer<S, A, I>(
  reducer: Reducer<S, A>,
  initialState: I & S,
  initializer: (arg: I & S) => S,
  options?: EnhancedReducerOptions<S>
): [S, Dispatch<A>, Subscribe<S, A>]
export function useReducer<S, A, I>(reducer: Reducer<S, A>, initialState: I & S, ...args: any[]) {
  const [arg1, arg2] = args
  const [init, options] = typeof arg1 === 'function' ? [arg1, arg2] : [undefined, arg1]

  // dev tools related
  const context = useContext(InspectorContext)
  const reduxDevTools = getDevTools()
  const shouldNotifyReduxTools = context?.enabled === true && options != null

  const [state, _dispatch] = useReactReducer(reducer, initialState, init)

  const name = options?.name
  // Redux DevTools handler
  const handleStateChange: Listener<S, A> = useCallback(
    ({ nextState, action }) => {
      if (shouldNotifyReduxTools && reduxDevTools) {
        const computedName = typeof name === 'function' ? name(nextState) : name
        reduxDevTools.send(action, nextState, { name: computedName })
      }
    },
    [name, reduxDevTools, shouldNotifyReduxTools]
  )

  // Use the queued dispatch hook
  const [dispatch, subscribe] = useQueuedDispatch({
    state,
    dispatch: _dispatch,
    reducer,
    // global access to state change
    onStateChange: handleStateChange,
  })

  return [state, dispatch, subscribe]
}
